import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LoadingScreen from "./LoadingScreen";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { useOrganization } from "../context/OrganizationContext";
import SubscriptionPlans from "./SubscriptionPlans";
import SubscriptionCurrentPlan from "./SubscriptionCurrentPlan";
import BillingHistory from "./BillingHistory";
import apiService from "../services/apiService";

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentOrganization,
    loading: orgLoading,
    subscription,
    updateOrganization,
  } = useOrganization();
  const [activeTab, setActiveTab] = useState("subscription");
  const [subscriptionSubTab, setSubscriptionSubTab] = useState("plans");
  /** When user has a plan: hide pricing grid until "Change plan". No plan: always show plans. */
  const [showPlansSection, setShowPlansSection] = useState(false);
  const [loading, setLoading] = useState(true);
  const userEmail = localStorage.getItem("userEmail") || "";

  const [passwordFormOpen, setPasswordFormOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [isEditingOrgName, setIsEditingOrgName] = useState(false);
  const [orgNameDraft, setOrgNameDraft] = useState("");
  const [orgNameSaving, setOrgNameSaving] = useState(false);
  const subscriptionPlansSectionRef = useRef(null);

  // Team members + invites state
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "member" });
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  // Per-invite Resend cooldown — mirrors the 60s server-side gate (kath@
  // incident: admin spam-clicked Resend, multiple identical emails landed).
  // Backend stamps `last_sent_at` and 429s within the window; we also start
  // the cooldown locally on click for instant UX.
  const INVITE_RESEND_COOLDOWN_SECONDS = 60;
  const [inviteCooldowns, setInviteCooldowns] = useState({}); // { [inviteId]: secondsRemaining }

  // Member-removal flow — owner-only, confirmation-gated. memberToRemove
  // holds the row from `members` that the owner clicked Remove on; the
  // modal is open while non-null. Backend rejects owner-removal and
  // self-removal; the UI hides the button in those cases anyway.
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [isRemovingMember, setIsRemovingMember] = useState(false);

  // Mirror the backend rule: only the org owner can create / revoke / resend
  // invites. Hide the controls for everyone else so they don't get a 403 in
  // their face. Backend still enforces, this is just UX.
  const currentMemberRole =
    members.find(
      (m) => (m.user_email || "").toLowerCase() === userEmail.toLowerCase()
    )?.role || null;
  const isOrgOwner = currentMemberRole === "owner";

  // Delete-account flow state
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      await apiService.deleteAccount();
      // Wipe all client-side auth state.
      localStorage.removeItem("authToken");
      localStorage.removeItem("token");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("currentOrganizationId");
      localStorage.removeItem("pendingVerificationEmail");
      localStorage.removeItem("pendingCheckoutPlanName");
      const deleteCookie = (name) => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
      };
      deleteCookie("user_session");
      deleteCookie("auth_token");
      toast.success("Your account has been deleted.");
      // Hard reload to clear any in-memory React Query / context state
      window.location.href = "/#/login";
    } catch (err) {
      toast.error(err?.message || "Failed to delete account.");
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const reloadTeam = useCallback(async () => {
    if (!currentOrganization?.id) return;
    try {
      const detail = await apiService.getOrganization(currentOrganization.id);
      setMembers(detail?.members || []);
    } catch (err) {
      // Already toasted by apiService
    }
    try {
      const invites = await apiService.listInvites(currentOrganization.id);
      setPendingInvites(
        (invites || []).filter((i) => i.status === "pending")
      );
    } catch (err) {
      // Already toasted by apiService
    }
  }, [currentOrganization?.id]);

  useEffect(() => {
    reloadTeam();
  }, [reloadTeam]);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!currentOrganization?.id) return;
    if (!inviteForm.email.trim()) {
      toast.error("Email is required");
      return;
    }
    setIsSendingInvite(true);
    try {
      await apiService.createInvite(currentOrganization.id, {
        email: inviteForm.email.trim(),
        role: inviteForm.role,
      });
      toast.success(`Invite sent to ${inviteForm.email}`);
      setShowInviteModal(false);
      setInviteForm({ email: "", role: "member" });
      reloadTeam();
    } catch (err) {
      // apiService toasts on error
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await apiService.revokeInvite(inviteId);
      toast.success("Invite revoked");
      reloadTeam();
    } catch (err) {
      // toasted
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!memberToRemove) return;
    const target = memberToRemove;
    // Optimistically drop the row so the list updates instantly. If the
    // server rejects we re-fetch to restore.
    setMembers((prev) => prev.filter((m) => m.id !== target.id));
    setIsRemovingMember(true);
    try {
      await apiService.removeMember(target.uuid || target.id);
      toast.success(`Removed ${target.user_email}`);
      setMemberToRemove(null);
    } catch (err) {
      // Pull a fresh members list to undo the optimistic drop.
      try {
        const detail = await apiService.getOrganization(currentOrganization.id);
        setMembers(detail?.members || []);
      } catch {
        /* swallow — toast below covers it */
      }
      const code = err?.response?.data?.code;
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to remove member.";
      toast.error(detail);
      // Keep the modal open on validation failures so the user can read
      // the message; close on transport errors so they can retry from
      // the row.
      if (code !== "cannot_remove_owner" && code !== "cannot_remove_self") {
        setMemberToRemove(null);
      }
    } finally {
      setIsRemovingMember(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    if ((inviteCooldowns[inviteId] || 0) > 0) return; // belt-and-suspenders
    // Optimistically arm the cooldown so a fast double-click can't fire
    // two requests before the server-side stamp lands.
    setInviteCooldowns((c) => ({ ...c, [inviteId]: INVITE_RESEND_COOLDOWN_SECONDS }));
    try {
      await apiService.resendInvite(inviteId);
      toast.success("Invite email resent");
    } catch (err) {
      // 429 with retryAfter → align cooldown with the server's countdown.
      if (err?.code === "throttled" && Number.isFinite(err.retryAfter)) {
        setInviteCooldowns((c) => ({ ...c, [inviteId]: err.retryAfter }));
        toast.error(
          `Please wait ${err.retryAfter}s before resending this invite.`
        );
      } else {
        // Non-cooldown failure — release the optimistic lock so the admin
        // can retry immediately rather than waiting out a phantom 60s.
        setInviteCooldowns((c) => ({ ...c, [inviteId]: 0 }));
        toast.error(err?.message || "Failed to resend invite");
      }
    }
  };

  // Seed cooldowns from each invite's last_sent_at so a page reload during
  // the 60s window still shows the correct remaining time.
  useEffect(() => {
    const next = {};
    const now = Date.now();
    for (const inv of pendingInvites) {
      if (!inv.last_sent_at) continue;
      const elapsed = (now - new Date(inv.last_sent_at).getTime()) / 1000;
      const remaining = Math.ceil(INVITE_RESEND_COOLDOWN_SECONDS - elapsed);
      if (remaining > 0) next[inv.id] = remaining;
    }
    setInviteCooldowns((c) => ({ ...next, ...c })); // preserve any optimistic locks
  }, [pendingInvites]);

  // Tick all active cooldowns down once per second.
  useEffect(() => {
    if (Object.values(inviteCooldowns).every((s) => s <= 0)) return undefined;
    const t = setInterval(() => {
      setInviteCooldowns((prev) => {
        const next = {};
        let any = false;
        for (const [k, v] of Object.entries(prev)) {
          const dec = v - 1;
          if (dec > 0) {
            next[k] = dec;
            any = true;
          }
        }
        return any ? next : {};
      });
    }, 1000);
    return () => clearInterval(t);
  }, [inviteCooldowns]);

  useEffect(() => {
    if (!currentOrganization?.id) return;
    if (subscription) {
      setSubscriptionSubTab("history");
      setShowPlansSection(false);
    } else {
      setSubscriptionSubTab("plans");
      setShowPlansSection(true);
    }
  }, [currentOrganization?.id, subscription?.id]);

  useEffect(() => {
    const requestedTab = location.state?.settingsTab;
    const requestedSubTab = location.state?.settingsSubTab;

    if (!requestedTab) return;

    if (
      requestedTab === "subscription" ||
      requestedTab === "account" ||
      requestedTab === "organization"
    ) {
      setActiveTab(requestedTab);
    }

    if (requestedTab !== "subscription") return;

    if (requestedSubTab === "plans") {
      setSubscriptionSubTab("plans");
      setShowPlansSection(true);
      return;
    }

    if (requestedSubTab === "history") {
      setSubscriptionSubTab("history");
      setShowPlansSection(false);
      return;
    }

    if (!subscription) {
      setSubscriptionSubTab("plans");
      setShowPlansSection(true);
    }
  }, [location.state, subscription]);

  useEffect(() => {
    setOrgNameDraft(currentOrganization?.name || "");
    setIsEditingOrgName(false);
  }, [currentOrganization?.id, currentOrganization?.name]);

  const resetPasswordForm = useCallback(() => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  }, []);

  const togglePasswordForm = useCallback(() => {
    setPasswordFormOpen((open) => {
      if (open) resetPasswordForm();
      return !open;
    });
  }, [resetPasswordForm]);

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordSaving) return;
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    setPasswordSaving(true);
    try {
      await apiService.changePassword({
        currentPassword,
        newPassword,
        newPasswordConfirm: confirmPassword,
      });
      toast.success("Password updated successfully");
      resetPasswordForm();
      setPasswordFormOpen(false);
    } catch (err) {
      const d = err?.response?.data;
      let msg = "Could not update password";
      if (typeof d?.detail === "string") msg = d.detail;
      else if (d && typeof d === "object") {
        const parts = [];
        Object.values(d).forEach((v) => {
          if (Array.isArray(v)) parts.push(...v.map(String));
          else if (typeof v === "string") parts.push(v);
        });
        if (parts.length) msg = parts.join(" ");
      } else if (err?.message) msg = err.message;
      toast.error(msg);
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSaveOrganizationName = async (e) => {
    e.preventDefault();
    if (!currentOrganization?.id || orgNameSaving) return;

    const nextName = orgNameDraft.trim();
    if (!nextName) {
      toast.error("Organization name cannot be empty");
      return;
    }

    if (nextName === (currentOrganization.name || "").trim()) {
      setIsEditingOrgName(false);
      return;
    }

    setOrgNameSaving(true);
    try {
      await updateOrganization(currentOrganization.id, { name: nextName });
      setIsEditingOrgName(false);
    } catch {
      // Context already shows the error toast.
    } finally {
      setOrgNameSaving(false);
    }
  };

  // Verify authentication
  useEffect(() => {
    const token = localStorage.getItem("token");
    const email = localStorage.getItem("userEmail");

    if (!token || !email) {
      navigate("/login");
      return;
    }

    setLoading(false);
  }, [navigate]);

  if (loading || orgLoading) {
    return <LoadingScreen />;
  }

  const tabs = [
    {
      id: "subscription",
      name: "Subscription",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
      ),
    },
    {
      id: "account",
      name: "Account",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
    },
    {
      id: "organization",
      name: "Organization",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
    },
  ];

  return (
    <div>
      <div className="min-h-screen bg-[#131313]">
        <header className="sticky top-0 z-10 border-b border-white/[0.06] bg-[#131313]">
          <div className="px-3 py-3 sm:px-6 sm:py-4 lg:px-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-title text-xl font-bold tracking-tight text-white sm:text-2xl">
                  Settings
                </h1>
              </div>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-screen-2xl px-3 py-2">
          <p className="font-secondary my-4 text-md text-white/65">
            Manage your account and subscription
          </p>
          <div className="overflow-visible rounded-sm border border-white/10 bg-landing-section p-4 sm:p-8">
            {/* Tabs */}
            <div className="mb-4 overflow-x-auto border-b border-white/10 sm:mb-6">
              <nav
                className="flex space-x-4 sm:space-x-8 min-w-max"
                aria-label="Tabs"
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`font-secondary flex items-center space-x-1.5 whitespace-nowrap px-1 pb-3 text-xs font-semibold transition-colors sm:space-x-2 sm:pb-4 sm:text-sm ${
                      activeTab === tab.id
                        ? "border-b-2 border-primary text-primary"
                        : "border-b-2 border-transparent text-white/50 hover:text-white/80"
                    }`}
                  >
                    {tab.icon}
                    <span>{tab.name}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
              {activeTab === "subscription" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="mb-6">
                    <h2 className="font-title text-xl font-semibold tracking-tight text-white">
                      Subscription management
                    </h2>
                    <p className="font-secondary mt-2 text-sm text-white/65">
                      Choose a plan that fits your needs. You can upgrade,
                      downgrade, or cancel at any time.
                    </p>
                  </div>

                  {/* Subscription Sub-Tabs */}
                  <div className="mb-6">
                    <SubscriptionCurrentPlan
                      onGoToPlans={() => {
                        setShowPlansSection(true);
                        setSubscriptionSubTab("plans");
                        setTimeout(() => {
                          subscriptionPlansSectionRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }, 0);
                      }}
                    />
                  </div>

                  {subscription && !showPlansSection ? (
                    <BillingHistory />
                  ) : (
                    <>
                      <div className="mb-6 border-b border-white/10">
                        <nav className="flex gap-6">
                          <button
                            type="button"
                            onClick={() => setSubscriptionSubTab("plans")}
                            className={`font-secondary border-b-2 pb-3 text-sm font-semibold transition-colors ${
                              subscriptionSubTab === "plans"
                                ? "border-primary text-primary"
                                : "border-transparent text-white/50 hover:text-white/80"
                            }`}
                          >
                            Available plans
                          </button>
                          <button
                            type="button"
                            onClick={() => setSubscriptionSubTab("history")}
                            className={`font-secondary border-b-2 pb-3 text-sm font-semibold transition-colors ${
                              subscriptionSubTab === "history"
                                ? "border-primary text-primary"
                                : "border-transparent text-white/50 hover:text-white/80"
                            }`}
                          >
                            Billing history
                          </button>
                        </nav>
                      </div>

                      {subscriptionSubTab === "plans" && (
                        <div
                          ref={subscriptionPlansSectionRef}
                          id="subscription-plans-section"
                        >
                          <SubscriptionPlans />
                        </div>
                      )}
                      {subscriptionSubTab === "history" && <BillingHistory />}
                    </>
                  )}
                </motion.div>
              )}

              {activeTab === "account" && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="font-title text-xl font-semibold tracking-tight text-white">
                      Account information
                    </h2>
                    <p className="font-secondary mb-6 mt-2 text-sm text-white/65">
                      Manage your personal account details
                    </p>
                  </div>

                  {/* Email Display */}
                  <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-5 sm:p-6">
                    <label className="font-secondary mb-2 block text-sm font-medium text-white/65">
                      Email address
                    </label>
                    <div className="flex flex-wrap items-center gap-3">
                      <svg
                        className="h-5 w-5 shrink-0 text-primary/80"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="font-secondary font-medium text-white">
                        {userEmail}
                      </p>
                      <span className="font-secondary inline-flex items-center rounded-sm border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-semibold text-emerald-300">
                        Verified
                      </span>
                    </div>
                  </div>

                  {/* Password change — expand to show form */}
                  <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-5 sm:p-6">
                    <label className="font-secondary mb-2 block text-sm font-medium text-white/65">
                      Password
                    </label>
                    <p className="font-secondary mb-4 text-sm text-white/50">
                      Change your password to keep your account secure
                    </p>
                    <motion.button
                      type="button"
                      onClick={togglePasswordForm}
                      aria-expanded={passwordFormOpen}
                      className="font-secondary rounded-sm border border-white/15 bg-transparent px-4 py-2.5 text-sm font-semibold text-white/80 transition-colors hover:bg-white/[0.06] hover:text-white"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {passwordFormOpen ? "Cancel" : "Change password"}
                    </motion.button>

                    <AnimatePresence initial={false}>
                      {passwordFormOpen && (
                        <motion.form
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                          onSubmit={handleChangePasswordSubmit}
                        >
                          <div className="mt-5 space-y-4 border-t border-white/10 pt-5">
                            <div>
                              <label
                                htmlFor="current-password"
                                className="font-secondary mb-1.5 block text-xs font-medium text-white/55"
                              >
                                Current password
                              </label>
                              <input
                                id="current-password"
                                type="password"
                                autoComplete="current-password"
                                value={currentPassword}
                                onChange={(e) =>
                                  setCurrentPassword(e.target.value)
                                }
                                required
                                className="font-secondary w-full rounded-sm border border-white/[0.12] bg-[#0a0a0a] px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-primary/45 focus:outline-none focus:ring-1 focus:ring-primary/25"
                                placeholder="Enter current password"
                              />
                            </div>
                            <div>
                              <label
                                htmlFor="new-password"
                                className="font-secondary mb-1.5 block text-xs font-medium text-white/55"
                              >
                                New password
                              </label>
                              <input
                                id="new-password"
                                type="password"
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                minLength={8}
                                className="font-secondary w-full rounded-sm border border-white/[0.12] bg-[#0a0a0a] px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-primary/45 focus:outline-none focus:ring-1 focus:ring-primary/25"
                                placeholder="At least 8 characters"
                              />
                            </div>
                            <div>
                              <label
                                htmlFor="confirm-new-password"
                                className="font-secondary mb-1.5 block text-xs font-medium text-white/55"
                              >
                                Confirm new password
                              </label>
                              <input
                                id="confirm-new-password"
                                type="password"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) =>
                                  setConfirmPassword(e.target.value)
                                }
                                required
                                minLength={8}
                                className="font-secondary w-full rounded-sm border border-white/[0.12] bg-[#0a0a0a] px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-primary/45 focus:outline-none focus:ring-1 focus:ring-primary/25"
                                placeholder="Repeat new password"
                              />
                            </div>
                            <div className="flex flex-wrap gap-3 pt-1">
                              <motion.button
                                type="submit"
                                disabled={passwordSaving}
                                className="font-secondary rounded-sm border border-primary bg-primary px-5 py-2.5 text-sm font-bold text-[#0a0a0a] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                                whileHover={
                                  passwordSaving ? {} : { scale: 1.02 }
                                }
                                whileTap={passwordSaving ? {} : { scale: 0.98 }}
                              >
                                {passwordSaving
                                  ? "Updating…"
                                  : "Update password"}
                              </motion.button>
                            </div>
                          </div>
                        </motion.form>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Danger Zone — temporarily disabled while we work out
                      the ownership-transfer flow for orgs with co-members.
                      Re-enable once that flow lands. Backend mirrors this
                      with a 503 on DELETE /api/auth/account/. */}
                  <div className="rounded-sm border border-white/10 bg-white/[0.03] p-5 sm:p-6">
                    <h3 className="font-title mb-2 text-base font-semibold text-white/70">
                      Delete account
                    </h3>
                    <p className="font-secondary text-sm text-white/55">
                      Account deletion is temporarily unavailable while we
                      finalise the ownership-transfer flow for organisations
                      with multiple members. Please contact{" "}
                      <a
                        href="mailto:nodes@nodefleet.net"
                        className="text-primary hover:underline"
                      >
                        nodes@nodefleet.net
                      </a>{" "}
                      if you need your account removed.
                    </p>
                  </div>

                  <AnimatePresence>
                    {showDeleteAccountModal && (
                      <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => !isDeletingAccount && setShowDeleteAccountModal(false)}
                      >
                        <motion.div
                          className="w-full max-w-md rounded-sm border border-red-500/30 bg-landing-section shadow-2xl"
                          initial={{ scale: 0.96, opacity: 0, y: 12 }}
                          animate={{ scale: 1, opacity: 1, y: 0 }}
                          exit={{ scale: 0.96, opacity: 0, y: 12 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="border-b border-red-500/20 p-6">
                            <h3 className="font-title text-xl font-bold text-red-300">
                              Delete account
                            </h3>
                            <p className="font-secondary mt-2 text-sm text-white/70">
                              This permanently deletes your account, the
                              organization you own, and every API key, project,
                              and subscription tied to it. This cannot be undone.
                            </p>
                          </div>
                          <div className="space-y-4 p-6">
                            <label className="block text-sm font-medium text-white/70">
                              Type your email to confirm
                              <span className="ml-2 font-mono text-xs text-red-200">
                                {userEmail}
                              </span>
                            </label>
                            <input
                              type="text"
                              autoFocus
                              value={deleteConfirmText}
                              onChange={(e) => setDeleteConfirmText(e.target.value)}
                              placeholder={userEmail}
                              className="font-secondary w-full rounded-sm border border-white/15 bg-[#0c0c0c] px-4 py-3 text-white placeholder:text-white/35 focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400/30"
                            />
                            <div className="flex justify-end gap-3 pt-2">
                              <button
                                type="button"
                                onClick={() => setShowDeleteAccountModal(false)}
                                disabled={isDeletingAccount}
                                className="font-secondary rounded-sm border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/70 hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleDeleteAccount}
                                disabled={
                                  isDeletingAccount ||
                                  deleteConfirmText.trim() !== userEmail
                                }
                                className="font-secondary rounded-sm border border-red-500/45 bg-red-500/20 px-4 py-2.5 text-sm font-bold text-red-200 hover:bg-red-500/30 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {isDeletingAccount ? "Deleting…" : "Delete account"}
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}

              {activeTab === "organization" && currentOrganization && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <div>
                    <h2 className="font-title text-xl font-semibold tracking-tight text-white">
                      Organization settings
                    </h2>
                    <p className="font-secondary mb-6 mt-2 text-sm text-white/65">
                      Manage your organization details and members
                    </p>
                  </div>

                  {/* Organization Name */}
                  <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-5 sm:p-6">
                    <label className="font-secondary mb-2 block text-sm font-medium text-white/65">
                      Organization name
                    </label>
                    {isEditingOrgName ? (
                      <form
                        onSubmit={handleSaveOrganizationName}
                        className="flex flex-col gap-3 sm:flex-row sm:items-start"
                      >
                        <div className="flex-1">
                          <input
                            type="text"
                            value={orgNameDraft}
                            onChange={(e) => setOrgNameDraft(e.target.value)}
                            className="font-secondary w-full rounded-sm border border-white/15 bg-[#0a0a0a] px-4 py-3 text-white placeholder:text-white/35 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                            placeholder="Organization name"
                            autoFocus
                          />
                        </div>
                        <div className="flex items-center gap-2 sm:pt-0.5">
                          <button
                            type="button"
                            onClick={() => {
                              setOrgNameDraft(currentOrganization.name || "");
                              setIsEditingOrgName(false);
                            }}
                            className="font-secondary rounded-sm border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/70 hover:bg-white/[0.06]"
                            disabled={orgNameSaving}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={orgNameSaving}
                            className="font-secondary rounded-sm border border-primary bg-primary px-4 py-2.5 text-sm font-bold text-[#0a0a0a] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {orgNameSaving ? "Saving..." : "Save"}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <svg
                            className="h-5 w-5 shrink-0 text-primary/80"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                          </svg>
                          <p className="font-secondary truncate font-medium text-white">
                            {currentOrganization.name}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsEditingOrgName(true)}
                          className="rounded-sm p-2 text-white/45 transition-colors hover:bg-white/[0.06] hover:text-primary"
                          aria-label="Edit organization name"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-2.586-9.414a2 2 0 112.828 2.828L11 16H8v-3l8.414-8.414z"
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Organization ID */}
                  <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-5 sm:p-6">
                    <label className="font-secondary mb-2 block text-sm font-medium text-white/65">
                      Organization ID
                    </label>
                    <div className="flex items-center gap-3">
                      <code className="rounded-sm border border-white/15 bg-[#0a0a0a] px-3 py-1.5 font-mono text-sm text-primary">
                        {currentOrganization.id}
                      </code>
                    </div>
                  </div>

                  {/* Team members + pending invites */}
                  <div className="rounded-sm border border-white/10 bg-[#0c0c0c] p-5 sm:p-6">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <div>
                        <h3 className="font-title text-lg font-semibold text-white">
                          Team members
                        </h3>
                        <p className="font-secondary mt-1 text-sm text-white/65">
                          Invite team members to collaborate on your projects.
                        </p>
                      </div>
                      {isOrgOwner ? (
                        <motion.button
                          type="button"
                          onClick={() => setShowInviteModal(true)}
                          className="font-secondary inline-flex shrink-0 items-center gap-2 rounded-sm border border-primary bg-primary px-4 py-2.5 text-sm font-bold text-[#0a0a0a] transition-colors hover:bg-primary-hover"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <span>Invite Member</span>
                        </motion.button>
                      ) : (
                        <span
                          className="font-secondary inline-flex shrink-0 items-center rounded-sm border border-white/10 bg-white/[0.04] px-3 py-2 text-xs text-white/55"
                          title="Only the organization owner can invite members."
                        >
                          Owner-only
                        </span>
                      )}
                    </div>

                    {/* Members list */}
                    <div className="mt-4">
                      <p className="font-secondary mb-2 text-xs uppercase tracking-[0.16em] text-white/45">
                        Members ({members.length})
                      </p>
                      <ul className="divide-y divide-white/[0.06] rounded-sm border border-white/10">
                        {members.length === 0 && (
                          <li className="font-secondary px-4 py-3 text-sm text-white/55">
                            No members yet.
                          </li>
                        )}
                        {members.map((m) => {
                          const isOwnerRow = m.role === "owner";
                          const isSelf = (m.user_email || "").toLowerCase() === userEmail.toLowerCase();
                          const canRemove = isOrgOwner && !isOwnerRow && !isSelf;
                          return (
                            <li
                              key={m.id}
                              className="flex items-center justify-between gap-3 px-4 py-3"
                            >
                              <div className="min-w-0">
                                <p className="font-secondary truncate text-sm font-medium text-white">
                                  {m.user_name || m.user_email}
                                </p>
                                <p className="font-secondary truncate text-xs text-white/45">
                                  {m.user_email}
                                </p>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <span className="font-secondary inline-flex rounded-sm border border-white/15 bg-white/[0.04] px-2 py-0.5 text-xs uppercase tracking-wider text-white/70">
                                  {m.role}
                                </span>
                                {canRemove && (
                                  <button
                                    type="button"
                                    onClick={() => setMemberToRemove(m)}
                                    className="font-secondary rounded-sm border border-red-500/30 px-3 py-1.5 text-xs font-medium text-red-300 transition-colors hover:bg-red-500/10"
                                    title={`Remove ${m.user_email} from this organization`}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    {/* Pending invites */}
                    {pendingInvites.length > 0 && (
                      <div className="mt-6">
                        <p className="font-secondary mb-2 text-xs uppercase tracking-[0.16em] text-white/45">
                          Pending invites ({pendingInvites.length})
                        </p>
                        <ul className="divide-y divide-white/[0.06] rounded-sm border border-white/10">
                          {pendingInvites.map((inv) => (
                            <li
                              key={inv.id}
                              className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="min-w-0">
                                <p className="font-secondary truncate text-sm font-medium text-white">
                                  {inv.email}
                                </p>
                                <p className="font-secondary text-xs text-white/45">
                                  {inv.role} · expires{" "}
                                  {new Date(inv.expires_at).toLocaleDateString()}
                                </p>
                              </div>
                              {isOrgOwner && (() => {
                                const cooldown = inviteCooldowns[inv.id] || 0;
                                const cooling = cooldown > 0;
                                return (
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleResendInvite(inv.id)}
                                      disabled={cooling}
                                      className={`font-secondary rounded-sm border px-3 py-1.5 text-xs font-medium transition-colors ${
                                        cooling
                                          ? "border-white/10 text-white/35 cursor-not-allowed"
                                          : "border-white/15 text-white/80 hover:bg-white/[0.06]"
                                      }`}
                                      title={
                                        cooling
                                          ? `Wait ${cooldown}s before resending`
                                          : "Send the invite email again"
                                      }
                                    >
                                      {cooling ? `Resend in ${cooldown}s` : "Resend"}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRevokeInvite(inv.id)}
                                      className="font-secondary rounded-sm border border-red-500/30 px-3 py-1.5 text-xs font-medium text-red-300 transition-colors hover:bg-red-500/10"
                                    >
                                      Revoke
                                    </button>
                                  </div>
                                );
                              })()}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Invite modal */}
                  <AnimatePresence>
                    {showInviteModal && (
                      <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => !isSendingInvite && setShowInviteModal(false)}
                      >
                        <motion.div
                          className="w-full max-w-md rounded-sm border border-white/10 bg-landing-section shadow-2xl"
                          initial={{ scale: 0.96, opacity: 0, y: 12 }}
                          animate={{ scale: 1, opacity: 1, y: 0 }}
                          exit={{ scale: 0.96, opacity: 0, y: 12 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="border-b border-white/10 p-6">
                            <h3 className="font-title text-xl font-bold text-white">
                              Invite team member
                            </h3>
                            <p className="font-secondary mt-2 text-sm text-white/60">
                              We&apos;ll email them a secure link to join {currentOrganization?.name}.
                            </p>
                          </div>
                          <form onSubmit={handleSendInvite} className="space-y-4 p-6">
                            <div>
                              <label className="mb-2 block text-sm font-medium text-white/70">
                                Email
                              </label>
                              <input
                                type="email"
                                required
                                value={inviteForm.email}
                                onChange={(e) =>
                                  setInviteForm({ ...inviteForm, email: e.target.value })
                                }
                                placeholder="teammate@example.com"
                                className="font-secondary w-full rounded-sm border border-white/15 bg-[#0c0c0c] px-4 py-3 text-white placeholder:text-white/35 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                              />
                            </div>
                            <div>
                              <label className="mb-2 block text-sm font-medium text-white/70">
                                Role
                              </label>
                              <select
                                value={inviteForm.role}
                                onChange={(e) =>
                                  setInviteForm({ ...inviteForm, role: e.target.value })
                                }
                                className="font-secondary w-full rounded-sm border border-white/15 bg-[#0c0c0c] px-4 py-3 text-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                              >
                                <option value="admin">Admin — manage members and projects</option>
                                <option value="member">Member — manage projects</option>
                                <option value="viewer">Viewer — read only</option>
                              </select>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                              <button
                                type="button"
                                onClick={() => setShowInviteModal(false)}
                                className="font-secondary rounded-sm border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/70 hover:bg-white/[0.06]"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                disabled={isSendingInvite}
                                className="font-secondary rounded-sm border border-primary bg-primary px-4 py-2.5 text-sm font-bold text-[#0a0a0a] hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {isSendingInvite ? "Sending..." : "Send invite"}
                              </button>
                            </div>
                          </form>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Remove-member confirmation modal — owner-only flow,
                      backend re-validates so this is purely UX. */}
                  <AnimatePresence>
                    {memberToRemove && (
                      <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => !isRemovingMember && setMemberToRemove(null)}
                      >
                        <motion.div
                          className="w-full max-w-md rounded-sm border border-white/10 bg-landing-section shadow-2xl"
                          initial={{ scale: 0.96, opacity: 0, y: 12 }}
                          animate={{ scale: 1, opacity: 1, y: 0 }}
                          exit={{ scale: 0.96, opacity: 0, y: 12 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="space-y-4 p-6">
                            <h3 className="font-title text-lg font-semibold text-white">
                              Remove member?
                            </h3>
                            <p className="font-secondary text-sm text-white/75">
                              <span className="font-semibold text-white">
                                {memberToRemove.user_name || memberToRemove.user_email}
                              </span>{" "}
                              ({memberToRemove.user_email}) will lose access to{" "}
                              <span className="font-semibold text-white">
                                {currentOrganization?.name}
                              </span>{" "}
                              immediately. Their account stays active and they
                              can be re-invited later.
                            </p>
                            <div className="flex justify-end gap-2 pt-2">
                              <button
                                type="button"
                                onClick={() => setMemberToRemove(null)}
                                disabled={isRemovingMember}
                                className="font-secondary rounded-sm border border-white/15 px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:bg-white/[0.06] disabled:opacity-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleConfirmRemoveMember}
                                disabled={isRemovingMember}
                                className="font-secondary rounded-sm border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm font-bold text-red-200 transition-colors hover:bg-red-500/20 disabled:opacity-50"
                              >
                                {isRemovingMember ? "Removing…" : "Remove"}
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Settings;
